import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableSortLabel,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    LinearProgress,
    Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {ProjectTableRow} from "./ProjectTableRow";
import {TableHeaderProps, TableProps} from "./TableTypes";

const tcSx = {
    padding: '4px 16px'
}

export function Table<T extends {id:string}>({sort, headers, page, size, pageChange, data, total, isLoading, mobileHeader }: TableProps<T>) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleChangePage = (_: unknown, newPage: number) => {
        pageChange(newPage, size);
    };
    const [tableWidth, setTableWidth] = useState(0);

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const newSize = parseInt(event.target.value, 10);
        pageChange(0, newSize);
    };

    const renderTitle = (title: TableHeaderProps<T>["title"]) => {
        if (typeof title === "function") return title();
        return title;
    };

    const renderMobileHeader = () => {
        if (!mobileHeader) return null;
        if (typeof mobileHeader === 'function') return mobileHeader();
        return mobileHeader;
    };

    const finalHeaders = useMemo(() => headers
        .filter(header => header.show === undefined
            || (typeof header.show === 'boolean' ? header.show : header.show())),
        [headers]
    );

    let paginationSize = 7;
    let loaderSize = finalHeaders.length - paginationSize;
    let loaderOnNextRow = false;
    if(paginationSize >= finalHeaders.length) {
        loaderOnNextRow = true;
        loaderSize = finalHeaders.length;
        paginationSize = finalHeaders.length;
    }

    const topScrollRef = useRef<HTMLDivElement|null>(null);
    const bottomScrollRef = useRef<HTMLDivElement|null>(null);
    const tableRef = useRef<HTMLTableElement|null>(null);

    useEffect(() => {
        const clb = () => tableRef.current && tableWidth !== tableRef.current.clientWidth && setTableWidth(tableRef.current.clientWidth);
        const interval = setInterval(() => clb(), 500)
        return () => {
            clearInterval(interval)
        }
    }, [tableRef, data, headers, tableWidth]);

    const syncScroll = (source:React.RefObject<HTMLDivElement|null>|null, target:React.RefObject<HTMLDivElement|null>|null) => {
        if(target && source) {
            if (target.current && source.current) {
                target.current.scrollLeft = source.current.scrollLeft;
            }
        }
    };

    // Mobile: render the same data via flex (no <table/>), but keep header.render(row) logic.
    if (isMobile) {
        const sortableHeaders = sort
            ? finalHeaders.filter(h => !h.disableSort)
            : [];

        const currentSortKey = sort?.key;
        const sortFieldValue = currentSortKey ? String(currentSortKey) : '';

        return (
            <Paper sx={{ width: "100%", overflow: "visible" }}>
                {isLoading && <LinearProgress/>}

                {/* Optional mobile header block (above sort) */}
                {mobileHeader && (
                    <Box sx={{ p: 1, pb: 0 }}>
                        {renderMobileHeader()}
                    </Box>
                )}

                {/* Mobile sort: select field + toggle direction */}
                {sort && sortableHeaders.length > 0 && (
                    <Box sx={{ p: 1, pb: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <FormControl fullWidth size="small">
                                <InputLabel id="table-sort-select-label">Sort</InputLabel>
                                <Select
                                    labelId="table-sort-select-label"
                                    value={sortFieldValue}
                                    label="Sort"
                                    onChange={(e) => {
                                        const key = String(e.target.value || '');
                                        if (!key) return;
                                        sort.onChange(key, (sort.order || 'asc') as any);
                                    }}
                                >
                                    {sortableHeaders.map((h) => {
                                        const key = String(h.sort || h.key);
                                        const title = renderTitle(h.title);
                                        return (
                                            <MenuItem key={key} value={key}>
                                                {title}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>

                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (!sort.key) return;
                                    const next = sort.order === 'asc' ? 'desc' : 'asc';
                                    sort.onChange(sort.key as any, next as any);
                                }}
                                sx={{
                                    border: '1px solid rgba(0,0,0,0.23)',
                                    borderRadius: 1,
                                    height: 40,
                                    width: 40,
                                }}
                            >
                                {sort.order === 'asc' ? <ArrowUpwardIcon fontSize="small"/> : <ArrowDownwardIcon fontSize="small"/>}
                            </IconButton>
                        </Stack>
                    </Box>
                )}

                <Stack sx={{ gap: 1, p: 1 }}>
                    {data.map((row, index) => (
                        <Stack
                            key={row.id || index}
                            sx={{
                                border: '1px solid rgba(0,0,0,0.12)',
                                borderRadius: 1,
                                p: 1,
                                gap: 1,
                            }}
                        >
                            {finalHeaders.map((header) => (
                                <Stack
                                    key={header.key}
                                    className={header.className}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    {!header.hideMobileTitle && (
                                        <Box sx={{ opacity: 0.7, minWidth: 110, display: 'flex', alignItems: 'center' }}>
                                            {renderTitle(header.title)}
                                        </Box>
                                    )}
                                    <Box sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                                        {isLoading ? <Skeleton width="100%"/> : (header.mobileRender ? header.mobileRender(row) : header.render(row))}
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>
                    ))}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={size}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </Box>
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper sx={{ width: "100%", overflow: "visible" }}>

            <div
                ref={topScrollRef}
                style={{ overflowX: "auto", overflowY: "hidden", position: "sticky", top: 0, zIndex: 2, background: theme.palette.background.paper, borderBottom: "1px solid rgba(0,0,0,0.08)" }}
                onScroll={() => syncScroll(topScrollRef, bottomScrollRef)}
            >
                {/* Spacer div to match table width */}
                <div style={{ width: tableWidth || "1200px", height: "1px" }}/>
            </div>
            <TableContainer ref={bottomScrollRef}
                             sx={{overflowX: "auto"}}
                             onScroll={() => syncScroll(bottomScrollRef, topScrollRef)}>
                <MuiTable sx={{minWidth: '1200px' }} ref={tableRef}>
                    <TableHead>
                        <TableRow>
                            {finalHeaders.map((header) => (
                                <TableCell
                                    key={header.key}
                                    align={header.alignment || "left"}
                                    className={header.className}
                                >
                                    {sort && !header.disableSort ? <TableSortLabel
                                        active={sort.key === header.key}
                                        direction={sort.order}
                                        onClick={() => sort?.onChange(header.sort || header.key, sort!.key === (header.sort || header.key) ? (sort!.order === 'asc' ? 'desc' : 'asc') : 'asc')}
                                    >
                                        {renderTitle(header.title)}
                                    </TableSortLabel> : renderTitle(header.title)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, index) => (
                            <ProjectTableRow loading={isLoading} headers={finalHeaders} row={row}
                                             key={row.id || index}/>
                         ))}
                        {loaderOnNextRow && <TableRow>
                            <TableCell sx={tcSx} colSpan={loaderSize}>
                                {isLoading && <LinearProgress/>}
                            </TableCell>
                        </TableRow>}
                        <TableRow>
                            {!loaderOnNextRow && <TableCell colSpan={loaderSize}>
                                {isLoading && <LinearProgress/>}
                            </TableCell>}
                            <TableCell colSpan={paginationSize}>
                                <TablePagination
                                    component="div"
                                    count={total}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={size}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </MuiTable>
            </TableContainer>
        </Paper>
    );
}
